import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Input, Stack, Text } from '@chakra-ui/react';
import { ListingFormLayout } from '@client/components/listingForm/ListingFormLayout';
import { ListingFormSkeleton } from '@client/components/listingForm/ListingFormSkeleton';
import { useListingForm, } from '@client/components/listingForm/useListingForm';
import { AppDialog } from '@client/components/misc/AppDialog';
import { DialogConfirmFooter } from '@client/components/misc/DialogConfirmFooter';
import { MoreActionsCollapsible } from '@client/components/misc/MoreActionsCollapsible';
import { CLIENT_ROUTES } from '@client/constants';
import { useApiClient } from '@client/hooks/useApiClient';
import { useMinDuration } from '@client/hooks/useMinDuration';
import { useUnsavedChangesGuard } from '@client/hooks/useUnsavedChangesGuard';
import { useShopManager } from '@client/providers/ShopManagerProvider';
import { toastError } from '@client/toaster';
import { callApi } from '@client/utils/apiUtils';
import { listingImageUrl } from '@client/utils/imageUtils';
import { useEffect, useRef, useState } from 'react';
import { FaCheckCircle, FaTrashAlt } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
const DeleteListingDialog = ({ open, title, pending, onCancel, onConfirm, }) => {
    const [confirmationText, setConfirmationText] = useState('');
    const canConfirm = confirmationText === title;
    const handleCancel = () => {
        setConfirmationText('');
        onCancel();
    };
    return (_jsx(AppDialog, { title: `Delete "${title}"?`, open: open, onCancel: handleCancel, pending: pending, size: "sm", titleProps: { truncate: true }, footer: _jsx(DialogConfirmFooter, { onCancel: handleCancel, onConfirm: onConfirm, confirmLabel: "Delete Listing", confirmColorPalette: "red", confirmDisabled: !canConfirm, pending: pending }), children: _jsxs(Stack, { gap: 3, fontSize: 18, children: [_jsx(Text, { children: "This will permanently delete the listing, including all of its variations. This action cannot be undone." }), _jsx(Text, { children: "Enter the listing title below to confirm." }), _jsx(Input, { value: confirmationText, onChange: (e) => setConfirmationText(e.target.value), disabled: pending, fontSize: 18, autoComplete: "off" })] }) }));
};
const ListingEditForm = ({ shopShortId, listingShortId, listingData, processingProfiles, shippingProfiles, returnProfiles, personalizationProfiles, }) => {
    const containerRef = useRef(null);
    const apiClient = useApiClient();
    const navigate = useNavigate();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletePending, setDeletePending] = useState(false);
    const [savePending, setSavePending] = useState(false);
    const initialImageEntries = listingData.imageUuids.map((uuid) => ({
        previewUrl: listingImageUrl(shopShortId, uuid),
        uuid,
        isUploading: false,
        uploadFailed: false,
    }));
    const form = useListingForm({
        shopShortId,
        initialTitle: listingData.title,
        initialSubtitle: listingData.subtitle ?? '',
        initialCategoryId: listingData.categoryId,
        initialCountryCode: listingData.countryCode,
        initialPriceCents: listingData.priceCents,
        initialTrackInventory: listingData.trackInventory,
        initialInventory: listingData.inventory != null
            ? String(listingData.inventory)
            : '',
        initialImageEntries,
        initialProcessingProfileId: listingData.processingProfileId != null
            ? String(listingData.processingProfileId)
            : null,
        initialShippingProfileId: listingData.shippingProfileId != null
            ? String(listingData.shippingProfileId)
            : null,
        initialReturnProfileId: listingData.returnProfileId != null
            ? String(listingData.returnProfileId)
            : null,
        initialPersonalizationProfileId: listingData.personalizationProfileId != null
            ? String(listingData.personalizationProfileId)
            : null,
        initialProcessingProfiles: processingProfiles,
        initialShippingProfiles: shippingProfiles,
        initialReturnProfiles: returnProfiles,
        initialPersonalizationProfiles: personalizationProfiles,
        initialDescrSections: listingData.fullDescr ?? [],
        initialVariations: listingData.variations,
        initialCombinations: listingData.combinations,
        initialVariationImages: listingData.variationImages,
    });
    useUnsavedChangesGuard(form.isDirty);
    const handleSave = async () => {
        if (!form.validate()) {
            requestAnimationFrame(() => {
                containerRef.current
                    ?.querySelector('[data-invalid]')
                    ?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            });
            return;
        }
        setSavePending(true);
        const result = await callApi(apiClient.shopManager.updateListing({
            params: { shopId: shopShortId, listingShortId },
            body: {
                title: form.title,
                subtitle: form.subtitle || null,
                categoryId: form.categoryId,
                countryCode: form.countryCode,
                priceCents: form.priceCents ?? 0,
                trackInventory: form.trackInventory,
                inventory: form.trackInventory
                    ? Number(form.inventory)
                    : null,
                imageUuids: form.uploadedUuids,
                processingProfileId: form.processingProfileId != null
                    ? Number(form.processingProfileId)
                    : null,
                shippingProfileId: form.shippingProfileId != null
                    ? Number(form.shippingProfileId)
                    : null,
                returnProfileId: form.returnProfileId != null
                    ? Number(form.returnProfileId)
                    : null,
                personalizationProfileId: form.personalizationProfileId != null
                    ? Number(form.personalizationProfileId)
                    : null,
                fullDescr: form.descrSections.length > 0
                    ? form.descrSections
                    : null,
                variations: form.variations,
                combinations: form.combinations,
                variationImages: form.variationImages,
            },
        }));
        setSavePending(false);
        if (result.error !== null) {
            toastError('Failed to save listing.', result.error);
            return;
        }
        navigate(`/${CLIENT_ROUTES.shop}/${shopShortId}/${CLIENT_ROUTES.manage}/${CLIENT_ROUTES.listings}`);
    };
    const handleDeleteConfirm = async () => {
        setDeletePending(true);
        const result = await callApi(apiClient.shopManager.deleteListing({
            params: { shopId: shopShortId, listingShortId },
            body: {},
        }));
        setDeletePending(false);
        if (result.error !== null) {
            toastError('Failed to delete listing. Please try again.');
            return;
        }
        setDeleteDialogOpen(false);
        navigate(`/${CLIENT_ROUTES.shop}/${shopShortId}/${CLIENT_ROUTES.manage}/${CLIENT_ROUTES.listings}`);
    };
    return (_jsxs(_Fragment, { children: [_jsx(ListingFormLayout, { form: form, containerRef: containerRef, disabled: savePending, actions: _jsxs(Stack, { gap: 5, children: [_jsxs(Button, { size: "lg", onClick: handleSave, disabled: !form.isDirty ||
                                form.isUploadingImages ||
                                savePending, loading: form.isUploadingImages || savePending, children: [_jsx(FaCheckCircle, {}), "Save Changes"] }), _jsx(MoreActionsCollapsible, { disabled: savePending, children: _jsxs(Button, { size: "md", fontSize: 18, colorPalette: "red", onClick: () => setDeleteDialogOpen(true), children: [_jsx(FaTrashAlt, {}), "Delete Listing"] }) })] }) }), _jsx(DeleteListingDialog, { open: deleteDialogOpen, title: listingData.title, pending: deletePending, onCancel: () => setDeleteDialogOpen(false), onConfirm: handleDeleteConfirm })] }));
};
export const ShopManagerListingEditPage = () => {
    const { shortId: shopShortId, listingShortId } = useParams();
    const apiClient = useApiClient();
    const { processingProfiles, shippingProfiles, returnProfiles, personalizationProfiles, profilesLoading, } = useShopManager();
    const [listingData, setListingData] = useState(null);
    const [rawListingLoading, setListingLoading] = useState(true);
    const listingLoading = useMinDuration(rawListingLoading);
    useEffect(() => {
        if (!shopShortId || !listingShortId)
            return;
        setListingLoading(true);
        callApi(apiClient.shopManager.getListing({
            params: { shopId: shopShortId, listingShortId },
        })).then((result) => {
            if (result.error === null)
                setListingData(result.data);
            setListingLoading(false);
        });
    }, [shopShortId, listingShortId]);
    if (listingLoading || profilesLoading || !listingData) {
        return _jsx(ListingFormSkeleton, {});
    }
    return (_jsx(ListingEditForm, { shopShortId: shopShortId, listingShortId: listingShortId, listingData: listingData, processingProfiles: processingProfiles, shippingProfiles: shippingProfiles, returnProfiles: returnProfiles, personalizationProfiles: personalizationProfiles }));
};
